import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowBack, 
  CalendarToday, 
  Person, 
  AccessTime, 
  Search,
  ExpandMore 
} from '@mui/icons-material';

interface GlossaryTerm {
  term: string;
  definition: string;
  category: string;
}

const AstrologyGlossaryScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const glossaryTerms: GlossaryTerm[] = [
    {
      term: '紫微斗数',
      definition: '中国古代占星学的重要分支，以紫微星为主星，结合其他星曜分析个人命运。',
      category: 'basics'
    },
    {
      term: '命盘',
      definition: '根据出生时间绘制的星盘图，显示各宫位和星曜的分布情况。',
      category: 'basics'
    },
    {
      term: '十二宫位',
      definition: '命盘中的十二个区域，分别代表人生的不同方面，如命宫、财帛宫、事业宫等。',
      category: 'structure'
    },
    {
      term: '星曜',
      definition: '紫微斗数中的各种星体，包括主星、辅星、煞星等，影响个人性格和命运。',
      category: 'structure'
    },
    {
      term: '主星',
      definition: '紫微斗数中的十四颗主要星曜，如紫微、天机、太阳、武曲等。',
      category: 'stars'
    },
    {
      term: '辅星',
      definition: '协助主星发挥作用的星曜，如左辅、右弼、文昌、文曲等。',
      category: 'stars'
    },
    {
      term: '四化',
      definition: '化禄、化权、化科、化忌四种变化，表示星曜在特定年份的能量转换。',
      category: 'advanced'
    },
    {
      term: '大限',
      definition: '十年为一大限，用于分析人生各个阶段的运势变化。',
      category: 'timing'
    },
    {
      term: '流年',
      definition: '每一年的运势分析，结合大限和本命盘进行综合判断。',
      category: 'timing'
    },
    {
      term: '流月',
      definition: '每个月的运势分析，更细致地预测短期运势变化。',
      category: 'timing'
    },
    {
      term: '三方四正',
      definition: '以某宫为中心，包括对宫、三合宫在内的四个宫位组合。',
      category: 'advanced'
    },
    {
      term: '庙旺利陷',
      definition: '星曜在不同宫位的强弱状态，影响其发挥的效果。',
      category: 'advanced'
    }
  ];

  const categories = [
    { id: 'all', label: '全部分类' },
    { id: 'basics', label: '基础概念' },
    { id: 'structure', label: '结构组成' },
    { id: 'stars', label: '星曜系统' },
    { id: 'timing', label: '时间系统' },
    { id: 'advanced', label: '高级概念' }
  ];

  const filteredTerms = glossaryTerms.filter(term => {
    const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleBackToBlog = () => {
    navigate('/blog');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToBlog}
            sx={{
              color: 'white',
              mb: 3,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            返回博客
          </Button>
          
          <Chip
            label="参考资料"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              mb: 2,
            }}
          />
          
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 'bold',
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            占星术语词汇表
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              flexWrap: 'wrap',
              opacity: 0.9,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
        <Typography variant="body2">紫微AI团队</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 18 }} />
              <Typography variant="body2">January 10, 2025</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 18 }} />
              <Typography variant="body2">8 min read</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'primary.main',
              fontWeight: 'medium',
              mb: 3,
            }}
          >
            术语词汇表
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 4 }}>
            这里收录了紫微斗数和中国传统占星学中的重要术语，帮助您更好地理解和学习这门古老的智慧。
          </Typography>

          {/* Search and Filter */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder="搜索术语..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {categories.map((category) => (
                <Chip
                  key={category.id}
                  label={category.label}
                  onClick={() => setSelectedCategory(category.id)}
                  variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                  color={selectedCategory === category.id ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Glossary Terms */}
        <Box>
          {filteredTerms.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                未找到匹配的术语
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {filteredTerms.map((term, index) => (
                <Grid item xs={12} key={index}>
                  <Accordion
                    sx={{
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: 1,
                      borderRadius: 1,
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        bgcolor: 'background.paper',
                        '&.Mui-expanded': {
                          minHeight: 48,
                        },
                        '& .MuiAccordionSummary-content': {
                          '&.Mui-expanded': {
                            margin: '12px 0',
                          },
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            color: 'primary.main',
                            flex: 1,
                          }}
                        >
                          {term.term}
                        </Typography>
                        <Chip
                          label={categories.find(cat => cat.id === term.category)?.label}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          lineHeight: 1.7,
                          color: 'text.secondary',
                        }}
                      >
                        {term.definition}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Call to Action */}
        <Paper
          sx={{
            mt: 6,
            p: 4,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
            了解更多紫微斗数知识
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            开始您的紫微斗数学习之旅，探索古老智慧的现代应用
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            开始学习之旅
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default AstrologyGlossaryScreen;